import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { useNavigateWithContentFlush } from '@/hooks';
import { useSelectedItemStore } from '@/store/selectedItemStore';
import { breadcrumbPathname, normalizeBreadcrumbTrail } from '@/utils/breadcrumb-state';
import { List } from 'lucide-react';
import { Fragment } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

export function ExploreNavBreadcrumbs() {
  const location = useLocation();
  const isExploreRoute = useMatch('/explore') !== null;
  const isSettingsRoute = useMatch('/settings/*') !== null;
  const isAdminRoute = useMatch('/admin') !== null;
  const isCalendarRoute = useMatch('/calendar') !== null;
  const isWork4MeRoute = useMatch('/work4me/*') !== null;
  const isContentRoute = useMatch('/content/:contentRef') !== null;
  const section = isSettingsRoute
    ? 'Settings'
    : isAdminRoute
      ? 'Admin'
      : isCalendarRoute
        ? 'Calendar'
        : isWork4MeRoute
          ? 'Work4Me'
          : isContentRoute
            ? 'Content'
            : isExploreRoute
              ? 'Explore'
              : 'Explore';
  const navigate = useNavigateWithContentFlush();
  const { clearSelectedItems } = useSelectedItemStore();
  const rawTrail = normalizeBreadcrumbTrail(location.state?.from);
  const isContentDetailRoute = location.pathname.startsWith('/content/');
  const formatLabel = (label: string) => label.replace(/^\w/, (c) => c.toUpperCase());

  return (
    <div data-testid="explore-breadcrumbs" className="flex items-center justify-between border-b p-1 lg:pr-4 lg:pl-1 ml-[1px] bg-muted/50">
      <div className="flex items-center">
        <Button 
          variant="ghost" 
          size="icon" 
          className="mr-2"
          onClick={() => {
            navigate('/explore');
            clearSelectedItems();
          }}
        >
          <List className="h-4 w-4" />
          <span className="sr-only">Back to list</span>
        </Button>
        {rawTrail.length > 0 ? (
          <Breadcrumb>
            <BreadcrumbList>
              {rawTrail.map((breadcrumb, index) => {
                const isLast = index === rawTrail.length - 1;
                const hrefMatchesCurrentPage =
                  isContentDetailRoute && breadcrumbPathname(breadcrumb.href) === location.pathname;
                const showAsLink =
                  (!isLast || isContentDetailRoute) && !(isContentDetailRoute && hrefMatchesCurrentPage);

                const handleCrumbClick = () => {
                  const nextFrom =
                    isContentDetailRoute && isLast ? rawTrail.slice(0, index) : rawTrail.slice(0, index + 1);
                  const nextState = { from: nextFrom };
                  navigate(breadcrumb.href, { state: nextState });
                  clearSelectedItems();
                };

                return (
                  <Fragment key={`${index}-${breadcrumb.href}`}>
                    {index > 0 ? <BreadcrumbSeparator /> : null}
                    {showAsLink ? (
                      <BreadcrumbLink asChild>
                        <span className="cursor-pointer" onClick={handleCrumbClick}>
                          {formatLabel(breadcrumb.label)}
                        </span>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbItem>{formatLabel(breadcrumb.label)}</BreadcrumbItem>
                    )}
                  </Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        ) : (
          <Breadcrumb>
            <BreadcrumbItem>{section}</BreadcrumbItem>
          </Breadcrumb>
        )}
      </div>
    </div>
  );
}
